import { Slot } from '@radix-ui/react-slot'
import { forwardRef, PropsWithChildren, useMemo, useRef, useState } from 'react'

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/Components/ui/tooltip'
import { useClickOutside } from '@/hooks/useClickOutside'
import { useIsMobile } from '@/hooks/useIsMobile'
import { cn } from '@/lib/utils'

interface Props extends PropsWithChildren {
  content: React.ReactNode
  side?: 'top' | 'bottom' | 'left' | 'right'
  triggerProps?: React.ComponentPropsWithoutRef<typeof TooltipTrigger>
  asChild?: boolean
  openOnClick?: boolean
  contentClassName?: string
  disableHover?: boolean
}
export const AppTooltip = forwardRef<HTMLButtonElement, Props>(
  ({ children, content, side = 'top', triggerProps = {}, contentClassName = '', disableHover = false }, ref) => {
    const [open, setOpen] = useState(false)
    const tooltipRef = useRef<HTMLDivElement>(null)
    const triggerRef = useRef<HTMLButtonElement>(null)
    const isMobile = useIsMobile()
    useClickOutside([tooltipRef, triggerRef], () => setOpen(false))

    const triggerPropsWithoutAsChild = useMemo(() => {
      const res = { ...triggerProps, asChild: undefined }
      delete res.asChild
      return res
    }, [triggerProps])

    if (disableHover || isMobile) {
      const Comp = triggerProps.asChild ? Slot : 'button'
      return (
        <TooltipProvider delayDuration={0}>
          <Tooltip open={open}>
            <TooltipTrigger asChild ref={triggerRef}>
              <Comp
                type='button'
                {...triggerPropsWithoutAsChild}
                className={cn('cursor-pointer', triggerProps.className)}
                onClick={() => setOpen(prev => !prev)}
                onKeyDown={e => {
                  e.preventDefault()
                  if (e.key === 'Enter') setOpen(!open)
                }}
              >
                {children}
              </Comp>
            </TooltipTrigger>
            <TooltipContent
              ref={tooltipRef}
              sideOffset={8}
              dir='rtl'
              className={cn(
                'flex max-w-[300px] flex-col gap-2 rounded-md text-sm font-normal text-white',
                contentClassName,
                !content ? 'hidden' : ''
              )}
              side={side}
            >
              <span className='inline-block'>{content}</span>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )
    }
    return (
      <Tooltip open={open} onOpenChange={setOpen}>
        <TooltipContent
          dir='rtl'
          className={cn('flex max-w-[300px] flex-col gap-2 rounded-2xl text-base text-white', contentClassName, !content && 'hidden')}
          side={side}
        >
          {content}
        </TooltipContent>
        <TooltipTrigger
          // onClick={() => {
          //   console.log('Click', open)
          //   setOpen(prev => !prev)
          // }}
          ref={ref}
          {...triggerProps}
        >
          {children}
        </TooltipTrigger>
      </Tooltip>
    )
  }
)

AppTooltip.displayName = 'AppTooltip'
